import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Briefcase, Plus, Search, MoreVertical, Edit, Trash2, Eye,
  Calendar, DollarSign, Users, Clock, CheckCircle, AlertCircle, XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  planning: { label: 'Planning', color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200', icon: AlertCircle },
  on_hold: { label: 'On Hold', color: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200', icon: XCircle },
};

const ProjectsPage = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'planning',
    customer_id: '',
    total_value: 0,
    start_date: '',
    target_completion_date: '',
    notes: ''
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchStats();
    fetchCustomers();
  }, [statusFilter]);

  const fetchProjects = async () => {
    try {
      const url = statusFilter 
        ? `${API_URL}/api/projects/?status=${statusFilter}`
        : `${API_URL}/api/projects/`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedProject(data);
        setShowDetailModal(true);
      }
    } catch (error) {
      toast.error('Failed to load project details');
    }
  };

  const handleCreate = async () => {
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/projects/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
          target_completion_date: formData.target_completion_date ? new Date(formData.target_completion_date).toISOString() : null
        })
      });

      if (response.ok) {
        toast.success('Project created');
        setShowCreateModal(false);
        resetForm();
        fetchProjects();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create project');
      }
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${selectedProject.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
          target_completion_date: formData.target_completion_date ? new Date(formData.target_completion_date).toISOString() : null
        })
      });

      if (response.ok) {
        toast.success('Project updated');
        setShowCreateModal(false);
        setIsEditing(false);
        resetForm();
        fetchProjects();
        fetchStats();
      } else {
        toast.error('Failed to update project');
      }
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Project deleted');
        fetchProjects();
        fetchStats();
      } else {
        toast.error('Failed to delete project');
      }
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const openEditModal = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name || '',
      description: project.description || '',
      status: project.status || 'planning',
      customer_id: project.customer_id || '',
      total_value: project.total_value || 0,
      start_date: project.start_date ? project.start_date.split('T')[0] : '',
      target_completion_date: project.target_completion_date ? project.target_completion_date.split('T')[0] : '',
      notes: project.notes || ''
    });
    setIsEditing(true);
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      status: 'planning',
      customer_id: '',
      total_value: 0,
      start_date: '',
      target_completion_date: '',
      notes: ''
    });
    setSelectedProject(null);
    setIsEditing(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Projects</h1>
            <p className="text-gray-600 dark:text-gray-300">Manage your active projects</p>
          </div>
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="add-project-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">Total Projects</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </CardContent>
            </Card>
            {Object.entries(STATUS_CONFIG).slice(0, 4).map(([key, config]) => (
              <Card key={key} className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter(key)}>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300">{config.label}</p>
                  <p className="text-xl font-bold">{stats.statuses?.[key] || 0}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant={statusFilter === '' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setStatusFilter('')}
          >
            All
          </Button>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <Button 
              key={key}
              variant={statusFilter === key ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setStatusFilter(key)}
            >
              {config.label}
            </Button>
          ))}
        </div>

        {/* Projects List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-600"></div>
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 dark:text-gray-400">No projects found</p>
                <Button className="mt-4" onClick={() => { resetForm(); setShowCreateModal(true); }}>
                  Create Your First Project
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="projects-table">
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Project</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Customer</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Value</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Timeline</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-gray-700">
                    {projects.map((project) => {
                      const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG.planning;
                      const StatusIcon = statusConfig.icon;
                      return (
                        <tr key={project.id} className="hover:bg-gray-50 dark:hover:bg-gray-800" data-testid={`project-row-${project.id}`}>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{project.name}</p>
                              {project.description && (
                                <p className="text-sm text-gray-500 truncate max-w-xs">{project.description}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {project.customer ? (
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <span className="text-blue-600 text-xs font-semibold">
                                    {project.customer.first_name?.[0]}{project.customer.last_name?.[0]}
                                  </span>
                                </div>
                                <span className="text-sm">
                                  {project.customer.first_name} {project.customer.last_name}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-medium">${project.total_value?.toLocaleString()}</span>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {project.start_date && (
                              <div className="text-gray-600 dark:text-gray-300">
                                Start: {new Date(project.start_date).toLocaleDateString()}
                              </div>
                            )}
                            {project.target_completion_date && (
                              <div className="text-gray-500 dark:text-gray-400">
                                Target: {new Date(project.target_completion_date).toLocaleDateString()}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => fetchProjectDetails(project.id)}>
                                  <Eye className="w-4 h-4 mr-2" /> View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEditModal(project)}>
                                  <Edit className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => handleDelete(project.id)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{isEditing ? 'Edit Project' : 'New Project'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-1">Project Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Smith Carport Installation"
                  data-testid="project-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Customer *</label>
                <select
                  className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                  value={formData.customer_id}
                  onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                  data-testid="project-customer"
                >
                  <option value="">Select customer...</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.first_name} {customer.last_name} {customer.company && `(${customer.company})`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Status</label>
                  <select
                    className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Value ($)</label>
                  <Input
                    type="number"
                    value={formData.total_value}
                    onChange={(e) => setFormData({ ...formData, total_value: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date</label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Target Completion</label>
                  <Input
                    type="date"
                    value={formData.target_completion_date}
                    onChange={(e) => setFormData({ ...formData, target_completion_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Description</label>
                <textarea
                  className="w-full border rounded-md px-3 py-2 min-h-[80px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Project description..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Notes</label>
                <textarea
                  className="w-full border rounded-md px-3 py-2 min-h-[60px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Internal notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button onClick={isEditing ? handleUpdate : handleCreate} data-testid="save-project-btn">
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Project Details</DialogTitle>
            </DialogHeader>
            {selectedProject && (
              <div className="space-y-4 py-4">
                <div>
                  <h3 className="text-xl font-bold">{selectedProject.name}</h3>
                  {selectedProject.description && (
                    <p className="text-gray-600 mt-1">{selectedProject.description}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedProject.status]?.color}`}>
                      {STATUS_CONFIG[selectedProject.status]?.label}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Value</p>
                    <p className="text-lg font-bold">${selectedProject.total_value?.toLocaleString()}</p>
                  </div>
                </div>

                {selectedProject.customer && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Customer</h4>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">
                          {selectedProject.customer.first_name?.[0]}{selectedProject.customer.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">
                          {selectedProject.customer.first_name} {selectedProject.customer.last_name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{selectedProject.customer.email}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Timeline</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Start Date</p>
                      <p>{selectedProject.start_date ? new Date(selectedProject.start_date).toLocaleDateString() : 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Target Completion</p>
                      <p>{selectedProject.target_completion_date ? new Date(selectedProject.target_completion_date).toLocaleDateString() : 'Not set'}</p>
                    </div>
                  </div>
                </div>

                {selectedProject.notes && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Notes</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{selectedProject.notes}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
              <Button onClick={() => {
                setShowDetailModal(false);
                openEditModal(selectedProject);
              }}>
                Edit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ProjectsPage;
